// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import InteractivePrototype from './interactivePrototype.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import Note from '../uTheory.Notation/note.ts';
import utils from '../utils.ts';

class PianoID extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = PianoID.registry.skills;
    this.settings = {
      instructions: true,
      piano: {
        listeners: false,
        ariaNoteDescriptionType: 'location',
        ariaLive: 'alert',
      },
      letters: { letterButtons: true },
      checkMark: {
        rightAnswerLength: 1,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      progressTracker: true,
      player: {},
    };

    this.layout = {
      instructions: { noWrap: true, maxPercent: 15 },
      piano: { percent: 45 },
      letters: { percent: 45 },
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    // If highest & lowest notes are not set, give us one full octave
    // and ignore octave to be sure things work:
    if (typeof options.lowestNote === 'undefined') options.lowestNote = 'C4';
    if (typeof options.highestNote === 'undefined') options.highestNote = 'C5';

    // copy our settings onto the prototype settings:
    utils.merge(this.settings.piano, {
      lowestNote: options.lowestNote,
      highestNote: options.highestNote,
    });

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions() {
    this.questions = [];
    this.questionPrompt =
      typeof this.options.questionPrompt !== 'undefined'
        ? this.options.questionPrompt
        : 'Click the name of the note you see.';

    this.note = new Note();

    if (this.options.drill) {
      this.options.questions = this.randomQuestionGenerator();
      this.questionPrompt += ` Number {question} of ${this.options.maxQuestions}`;
    }

    this.options.questions.forEach((question) => {
      const text = this.questions.length + 1;
      this.questions.push({ skills: [this.calculateSkill(question)], text: [`${text}`] });
    }, this);
    this.maxQuestions = this.questions.length;
  }

  randomQuestionGenerator() {
    /* Levels:
      1: C-G
      2: All white notes
      9: Sharps
      10: Flats
      11: Double Sharps
      12: Double Flats
    */
    const lowestNote = new Note(this.options.lowestNote);
    const highestNote = new Note(this.options.highestNote);

    const skills = this.options.skills;
    const skillObjects = [];
    skills.forEach((skill) => {
      const skillString = Skills.makeString(skill);
      let probability;
      switch (skillString) {
        case 'piano_c_g':
          probability = 5;
          break;
        case 'piano_a_b':
          probability = 2;
          break;
        case 'double_sharps':
          probability = 3;
          break;
        case 'double_flats':
          probability = 3;
          break;
        default:
          probability = 7;
          break;
      }
      skillObjects.push({ skill: skillString, probability });
    }, this);

    const generator = new Skills.QuestionGenerator();
    return generator.multipleSkillsGenerate(skillObjects, this.options.maxQuestions, {
      highestNote,
      lowestNote,
    });
  }

  addListeners() {
    this.letterPanel.addLetterCallback(this.check, this);
  }

  calculateSkill(noteName) {
    if (!this.skillCalculator) {
      this.skillCalculator = new SkillCalculator();
    }
    return this.skillCalculator.getPianoSkill(noteName);
  }

  newQuestion() {
    if (this.questionNumber > 0) window.setTimeout(this.newQuestionDelayer.bind(this), 750);
    else this.newQuestionDelayer();
  }

  newQuestionDelayer() {
    this.piano.clearColors();
    this.piano.clearNoteLabels();
    this.piano.options.ariaLive = 'alert';
    super.newQuestion();
    this.note.setName(this.options.questions[this.questionNumber]);
    this.piano.colorNote(this.note.getMidiNumber());
    this.letterPanel.setLettersAccidental(this.note.accidental);
    if (this.note.accidental !== '')
      this.piano.labelNote(this.note, this.note.toFontAccidentalUnformatted());
  }

  check(letter) {
    if (this.rightAnswerDelay) return;
    if (this.optionsTrue('testMode')) {
      this.checkpointAnswer(letter);
      return;
    }
    if (this.note.letter === letter) {
      this.piano.options.ariaLive = 'off';
      this.piano.labelNote(this.note, this.note.toAccidentalFontShortStringUnformatted());
      this.rightAnswer(0);
    } else this.wrongAnswer();
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.piano.clearColors();
      this.piano.clearNoteLabels();
      this.question = questionString;
      const note = new Note(questionString);
      this.note = note;
      this.piano.colorNote(note);
      if (note.accidental !== '') this.piano.labelNote(note, note.toFontAccidentalUnformatted());
      this.letterPanel.setLettersAccidental(note.accidental);

      const questionText = 'What is the name of the highlighted note?';
      this.instructionsDiv.innerHTML = questionText;

      this.checkpointQuestion = {
        note: this.note,
      };
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  getAnswerAndData(letter) {
    const unanswered = typeof letter === 'undefined';
    const correct = !unanswered && this.note.letter === letter;
    const skill = this.calculateSkill(this.note);
    const SVG = this.piano.exportSVG();
    const answerNote = unanswered ? null : new Note(letter + this.note.accidental);

    const answerAndData = {
      question: this.question,
      questionType: 'PianoID',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: SVG,
      studentAnswerText: unanswered ? null : answerNote.toAccidentalFontShortString(),
      studentAnswerSVG: null,
      correctAnswerText: this.note.toAccidentalFontShortString(),
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: skill,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    try {
      this.displayDiv.removeChild(this.lettersDiv);
    } catch (err) {
      // Swallow it!
    }
    super.gameOver();
  }

  playAnswer() {
    this.player.play(this.note, { startTime: 0, stopTime: 0.9 });
  }
}

PianoID.registry = {
  interactive: 'PianoID',
  description: 'Identify notes on the piano',
  skills: ['piano_c_g', 'piano_a_b', 'sharps', 'flats', 'double_sharps', 'double_flats'],
  options: {
    drill: true,
    lowestNote: 'G3',
    highestNote: 'C5',
    maxQuestions: 10,
    ignoreOctave: true,
  },
};

export default PianoID;
